import React from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {GenericPaginatedListRTK} from 'app/components/general/lists/GenericPaginatedListRTK';
import {WarnPublicPaidRequiredMessage} from 'app/components/general/WarnPublicPaidRequiredMessage';
import {useCurrentUser} from 'app/current-user/api';
import StateButton from 'app/general/widgets/StateButton';
import {useCreateProduct, useProducts} from 'app/market/api';
import {useAccountSettingsState} from 'app/settings/api';
import {TeamPageFilterBarStructure} from 'app/teams/components/TeamDetailPage/components/TeamPageFilterBarStructure';
import {TeamProductItem} from 'app/teams/components/TeamDetailPage/components/TeamProductItem';
import {gt} from 'app/utils/i18n';

import type {ListItemProps} from 'app/teams/components/TeamDetailPage/components/TeamListItem';
import type {
  AuthenticatedUserSerializedType,
  ProductSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type Props = {
  team: TeamSerializedType;
};

export const TeamProductsSubPage = ({team}: Props) => {
  const [createProduct] = useCreateProduct();
  const account = useAccountSettingsState().data!;

  const {data: currentUser} = useCurrentUser(undefined, {
    refetchOnMountOrArgChange: false,
  });

  function onCreateProduct(): Promise<unknown> {
    return createProduct({
      team: team.url,
      html_url: team.html_url,
    }).unwrap();
  }

  return (
    <div>
      {team.can_edit ? (
        <TeamPageFilterBarStructure>
          <div className="ml-auto col-auto align-items-center d-flex">
            <AddProductButton
              className="btn-light"
              currentUser={currentUser}
              onCreateProduct={onCreateProduct}
            />
          </div>
        </TeamPageFilterBarStructure>
      ) : null}

      <WarnPublicPaidRequiredMessage
        is_paying={account.is_paying}
        contentType={gt('product')}
      />
      <GenericPaginatedListRTK
        ItemComponent={ProductListItem}
        useQuery={useProducts}
        WrapperComponent={ContainerDiv}
        paramsBuilder={({location: {query}}) => ({
          team: team.url,
          ordering: query.ordering,
        })}
        noItemsMessage={(perms, NoResultsMessage) => (
          <NoResultsMessage head={gt('No products for this team')}>
            <>
              <p>
                {gt(
                  'Products are used to share the procedures, processes and policies in a team from one account to another.'
                )}
              </p>

              <LazyTooltip
                hide={currentUser?.is_super_manager === true}
                tooltip={gt('You must be super manager')}
              >
                <AddProductButton
                  className="btn-outline-primary"
                  currentUser={currentUser}
                  onCreateProduct={onCreateProduct}
                />
              </LazyTooltip>
            </>
          </NoResultsMessage>
        )}
      />
    </div>
  );
};

interface AddProductProps {
  currentUser?: AuthenticatedUserSerializedType;
  className: string;
  onCreateProduct: () => Promise<unknown>;
}

function AddProductButton({
  className,
  currentUser,
  onCreateProduct,
}: AddProductProps) {
  return (
    <StateButton
      className={cs('btn', className)}
      disabled={currentUser?.is_super_manager !== true}
      onClick={onCreateProduct}
      busyText={gt('Adding product...')}
    >
      <i className="fas fa-plus-circle mr-1" />
      {gt('Add Product')}
    </StateButton>
  );
}

const ProductListItem = ({object}: ListItemProps<ProductSerializedType>) => (
  <TeamProductItem product={object} />
);

const ContainerDiv = styled.div.attrs({className: 'container-fluid'})``;
